import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { db, auth } from 'services/firebase'; 
import { onAuthStateChanged } from 'firebase/auth';
import { 
  collection, onSnapshot, updateDoc, doc, addDoc, 
  query, where, serverTimestamp, arrayUnion 
} from 'firebase/firestore';

const GlobalContext = createContext();

export const GlobalProvider = ({ children }) => {
  // --- 1. ESTADOS ---
  const [ninos, setNinos] = useState([]);
  const [alertasAdmin, setAlertasAdmin] = useState([]);
  const [eventosGlobal, setEventosGlobal] = useState([]);
  const [usuarioCargado, setUsuarioCargado] = useState(false);

  // Estados fijos (puedes moverlos a Firebase después)
  const [salones, setSalones] = useState([
    { id: 's1', nombre: 'CUNA A', maestro: 'Sarahí Méndez', ninos: 0, estado: 'ok' },
    { id: 's2', nombre: 'PÁRVULOS B', maestro: 'Luis Rodríguez', ninos: 0, estado: 'ok' }
  ]);
  const [maestrosGlobal, setMaestrosGlobal] = useState([
    { id: 'm1', nombre: 'Luis Alberto García', rol: 'Maestro Titular', estatus: 'Activo', aptitudes: ['Manualidades', 'Primeros Auxilios'] }
  ]);

  // --- 2. CONTROL DE FLUJO (SEGURIDAD + REALTIME) ---
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUsuarioCargado(true);

        // Listener de Niños (Solo los que tiene permiso de ver)
        const qNinos = query(
          collection(db, "ninos"), 
          where("usuariosAutorizados", "array-contains", user.uid)
        );

        const unsubNinos = onSnapshot(qNinos, (snapshot) => {
          setNinos(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (err) => console.error("Error permisos niños:", err));

        // Listener de Alertas
        const qAlertas = query(
          collection(db, "alertas"), 
          where("tutoresAutorizados", "array-contains", user.uid),
          where("status", "==", "pendiente")
        );

        const unsubAlertas = onSnapshot(qAlertas, (snapshot) => {
          setAlertasAdmin(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        return () => { unsubNinos(); unsubAlertas(); };
      } else {
        setUsuarioCargado(false);
        setNinos([]);
        setAlertasAdmin([]);
      }
    });
    return () => unsubscribeAuth();
  }, []);

  // --- 3. LÓGICA DE NEGOCIO (Tus funciones originales corregidas) ---

  const registrarEntrada = useCallback(async (ninoId, datosSalud = {}, ticketGenerado) => {
    const ninoRef = doc(db, "ninos", ninoId);
    await updateDoc(ninoRef, {
      estatus: 'en_salon',
      ticket: ticketGenerado,
      ultimaTemp: datosSalud.temperatura || '36.5',
      notasSanitarias: datosSalud.notas || '',
      especial: parseFloat(datosSalud.temperatura) >= 37.5,
      horaEntrada: new Date().toISOString()
    });
  }, []);

  const registrarSalida = useCallback(async (ninoId) => {
    const ninoRef = doc(db, "ninos", ninoId);
    await updateDoc(ninoRef, {
      estatus: 'retirado',
      ticket: null,
      horaSalida: new Date().toISOString()
    });
  }, []);

  const crearAlerta = useCallback(async (nino, tipo, comentario) => {
    await addDoc(collection(db, "alertas"), {
      ninoId: nino.id,
      nino: nino.nombre,
      tipo,
      reporteMaestro: comentario,
      status: 'pendiente',
      tutoresAutorizados: nino.usuariosAutorizados || [], // Para que el padre reciba la alerta
      createdAt: serverTimestamp()
    });
  }, []);

  const actualizarPuntos = useCallback(async (ninoId, puntos) => {
    const ninoRef = doc(db, "ninos", ninoId);
    const ninoActual = ninos.find(n => n.id === ninoId);
    await updateDoc(ninoRef, {
      estrellas: (ninoActual?.estrellas || 0) + puntos
    });
  }, [ninos]);

  // Nueva función para el código familiar
  const vincularCodigoFamiliar = useCallback(async (codigoHijo) => {
    if (!auth.currentUser) return;
    const ninoRef = doc(db, "ninos", codigoHijo);
    await updateDoc(ninoRef, {
      usuariosAutorizados: arrayUnion(auth.currentUser.uid)
    });
  }, []);

  return (
    <GlobalContext.Provider value={{
      ninos, alertasAdmin, salones, maestrosGlobal, usuarioCargado,
      registrarEntrada, registrarSalida, crearAlerta, actualizarPuntos, vincularCodigoFamiliar
    }}>
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobal = () => useContext(GlobalContext);